/**
 * One person's carried setup, for somebody holding its link.
 *
 * Public in the strong sense, the way `Shared` is: it paints for a reader with
 * no account, no organisation and no membership, because holding the token is
 * the whole authorisation.
 *
 * This is a page a person opens to check they were sent the right thing. The
 * thing that installs it reads `/agents/install-a-setup` and the JSON behind
 * this, so nothing here is written for a machine.
 */
import { useEffect, useState } from 'react'
import { ApiError, api } from '../api/client'
import type { SharedSetup as Record } from '../api/types'
import { Face, Mark } from '../components/primitives'
import { radius } from '../components/primitives/shape'

export function SharedSetup({ token }: { token: string }) {
  const [state, setState] = useState<
    { status: 'loading' }
    | { status: 'gone' }
    | { status: 'unreachable'; detail: string }
    | { status: 'ready'; record: Record }
  >({ status: 'loading' })

  useEffect(() => {
    const tag = document.createElement('meta')
    tag.name = 'robots'
    tag.content = 'noindex, nofollow'
    document.head.appendChild(tag)
    return () => tag.remove()
  }, [])

  useEffect(() => {
    let live = true
    api.sharedSetup(token)
      .then((record) => live && setState({ status: 'ready', record }))
      .catch((error) => {
        if (!live) return
        setState(error instanceof ApiError && error.status === 404
          ? { status: 'gone' }
          : { status: 'unreachable', detail: String(error) })
      })
    return () => { live = false }
  }, [token])

  if (state.status === 'loading') {
    return <Frame><p role="status" style={{ color: 'var(--text-dim)' }}>Loading…</p></Frame>
  }
  if (state.status === 'gone') {
    return (
      <Frame>
        <h1 style={heading}>This link does not open anything.</h1>
        <p style={prose}>
          It was either withdrawn by whoever shared it, replaced with a newer
          one, or never a link at all. Ask them for a fresh one.
        </p>
      </Frame>
    )
  }
  if (state.status === 'unreachable') {
    return (
      <Frame>
        <h1 style={heading}>stagepost is not answering.</h1>
        <p style={prose}>This is the server, not your link. Try again shortly.</p>
      </Frame>
    )
  }

  const { record } = state
  return (
    <Frame>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 }}>
        <Face name={record.display_name} avatarKey={record.avatar_key} size={44} />
        <div style={{ minWidth: 0 }}>
          <h1 style={{ ...heading, margin: 0, overflowWrap: 'anywhere' }}>{record.display_name}&rsquo;s setup</h1>
          <p style={{ ...prose, fontSize: 13.5 }}>
            {record.items.length}{' '}
            {record.items.length === 1 ? 'item' : 'items'}, shared with whoever
            holds this link.
          </p>
          <a
            href={api.sharedSetupExportUrl(token)}
            download
            style={{ display: 'inline-block', marginTop: 6, fontSize: 13.5, fontWeight: 600, color: 'var(--accent-bright)' }}
          >
            Download setup
          </a>
        </div>
      </div>

      <p style={{ ...prose, marginBottom: 26 }}>
        Hand this page to your coding agent.{' '}
        <a href="/agents/install-a-setup" style={{ color: 'var(--accent-bright)', fontWeight: 600 }}>
          There is a page written for it
        </a>{' '}
        saying what each of these is and what to do with it. Nothing installs on
        its own, and nothing is written without you seeing the diff first.
      </p>

      {record.items.map((entry) => (
        <div
          key={`${entry.kind}/${entry.name}`}
          style={{ padding: '11px 0', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}
        >
          <span style={{ flex: 1, minWidth: 140, fontSize: 14, fontWeight: 700, overflowWrap: 'anywhere' }}>
            {entry.name}
          </span>
          <span style={{ font: '500 10px var(--font-machine)', letterSpacing: '.08em', textTransform: 'uppercase', padding: '3px 7px', borderRadius: radius.xs, color: 'var(--text-dim)', background: 'var(--panel-strong)' }}>
            {entry.kind}
          </span>
          <span style={mono}>
            {entry.origin
              ?? `${entry.files.length} ${entry.files.length === 1 ? 'file' : 'files'}`}
          </span>
        </div>
      ))}
    </Frame>
  )
}

const heading = {
  fontFamily: 'var(--font-human)', fontSize: 26, fontWeight: 500,
  margin: '0 0 10px', lineHeight: 1.25,
} as const

const prose = {
  fontFamily: 'var(--font-human)', fontSize: 15, lineHeight: 1.6,
  color: 'var(--text-dim)', margin: 0, maxWidth: '54ch',
} as const

const mono = {
  font: '400 11.5px var(--font-machine)', color: 'var(--text-faint)',
  overflowWrap: 'anywhere',
} as const

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100dvh', background: 'var(--ground)' }}>
      <div style={{ maxWidth: 780, margin: '0 auto', padding: '28px 22px 64px' }}>
        <a
          href="/"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 9, marginBottom: 30, textDecoration: 'none', color: 'var(--text-dim)' }}
        >
          <Mark />
          <span style={{ fontSize: 13.5, fontWeight: 700 }}>stagepost</span>
        </a>
        {children}
      </div>
    </div>
  )
}
